import React, { useState, useEffect } from 'react'
import { ArrowLeft, Save, Upload, X, Paperclip } from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import supportTicketService from '../../services/supportTicketService'
import userService from '../../services/userService'
import customerMasterService from '../../services/customerMasterService'

const STATUS_OPTIONS = ['Open', 'Assigned', 'In Progress', 'Waiting for Customer', 'In Review', 'Resolved', 'Closed', 'Inactive']
const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical']
const TYPE_OPTIONS = ['Bug', 'Feature', 'Question', 'Incident', 'Task']
const MAX_FILE_SIZE = 5 * 1024 * 1024
const MAX_FILES = 5

const SupportTicketEdit = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const location = useLocation()
  const ticketData = location.state?.ticketData

  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(true)
  const [users, setUsers] = useState([])
  const [customers, setCustomers] = useState([])
  const [attachments, setAttachments] = useState([])
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    status: 'Open',
    priority: 'Medium',
    type: 'Task',
    category: '',
    assignee: '',
    customer: '',
    dueDate: '',
    labels: ''
  })

  useEffect(() => {
    if (!id) {
      toast.error('Invalid ticket ID')
      navigate('/support/support-tickets')
      return
    }
    loadData()
  }, [id])

  const loadData = async () => {
    try {
      setFetching(true)
      const [ticketRes, usersRes, customersRes] = await Promise.all([
        supportTicketService.getById(id),
        userService.getAllUsers({ limit: 200 }),
        customerMasterService.getAllCustomers({ limit: 500 })
      ])

      if (usersRes?.data) setUsers(Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.users || [])
      if (customersRes?.success && customersRes?.data)
        setCustomers(Array.isArray(customersRes.data) ? customersRes.data : [])

      const t = ticketRes.success ? ticketRes.data : ticketData
      if (!t) {
        toast.error('Ticket not found')
        navigate('/support/support-tickets')
        return
      }

      setFormData({
        subject: t.subject || '',
        description: t.description || '',
        status: t.status || 'Open',
        priority: t.priority || 'Medium',
        type: t.type || 'Task',
        category: t.category || '',
        assignee: t.assignee?._id || t.assignee?.id || t.assignee || '',
        customer: t.customer?._id || t.customer?.id || t.customer || '',
        dueDate: t.dueDate ? new Date(t.dueDate).toISOString().split('T')[0] : '',
        labels: Array.isArray(t.labels) ? t.labels.join(', ') : (t.labels || '')
      })

      const existing = (t.attachments || []).map((a) => ({
        fileName: a.fileName,
        contentType: a.contentType,
        size: a.size,
        url: a.url,
        data: a.url,
        _existing: true
      }))
      setAttachments(existing)
    } catch (e) {
      console.error('Error loading ticket:', e)
      toast.error('Failed to load ticket')
      navigate('/support/support-tickets')
    } finally {
      setFetching(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || [])
    if (attachments.length + files.length > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} files allowed`)
      return
    }
    files.forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} exceeds 5MB limit`)
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        setAttachments((prev) => [
          ...prev,
          { fileName: file.name, contentType: file.type, size: file.size, data: reader.result }
        ])
      }
      reader.readAsDataURL(file)
    })
    e.target.value = ''
  }

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.subject?.trim()) {
      toast.error('Subject is required')
      return
    }
    try {
      setLoading(true)
      const payload = {
        subject: formData.subject.trim(),
        description: formData.description?.trim() || '',
        status: formData.status,
        priority: formData.priority,
        type: formData.type,
        category: formData.category?.trim() || '',
        assignee: formData.assignee || undefined,
        customer: formData.customer || undefined,
        dueDate: formData.dueDate || undefined,
        labels: formData.labels
          ? formData.labels.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
        attachments: attachments.map((a) => ({
          fileName: a.fileName,
          contentType: a.contentType,
          size: a.size,
          url: a.data || a.url
        }))
      }
      const res = await supportTicketService.update(id, payload)
      if (res.success) {
        toast.success('Support ticket updated successfully!')
        navigate('/support/support-tickets')
      } else {
        toast.error(res.message || 'Failed to update ticket')
      }
    } catch (error) {
      console.error('Error updating ticket:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data')
      } else {
        toast.error('Failed to update support ticket')
      }
    } finally {
      setLoading(false)
    }
  }

  const ticketId = ticketData?.ticketId || 'Ticket'

  const breadcrumbItems = [
    { label: 'Support', path: '/support' },
    { label: 'Support Tickets', path: '/support/support-tickets' },
    { label: `Edit ${ticketId}`, isCurrent: true }
  ]

  if (fetching) {
    return (
      <div className="sales-dashboard">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading ticket...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>
      <div className="page-header">
        <div className="page-title">
          <h1>Edit Support Ticket</h1>
          <p>Update ticket {ticketId}</p>
        </div>
      </div>
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Ticket Details</h2>
        </div>
        <form onSubmit={handleSubmit} className="form-content">
          <div className="section-header">
            <h3>Ticket Information</h3>
          </div>
          <div className="grid-row-4">
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="subject" className="form-label required">Subject</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="form-input"
                placeholder="Brief summary of the issue"
                required
              />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="description" className="form-label">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="form-input"
                rows={4}
                placeholder="Detailed description"
                style={{ resize: 'vertical' }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="type" className="form-label">Type</label>
              <select id="type" name="type" value={formData.type} onChange={handleChange} className="form-input">
                {TYPE_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="priority" className="form-label">Priority</label>
              <select id="priority" name="priority" value={formData.priority} onChange={handleChange} className="form-input">
                {PRIORITY_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="status" className="form-label">Status</label>
              <select id="status" name="status" value={formData.status} onChange={handleChange} className="form-input">
                {STATUS_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="category" className="form-label">Category</label>
              <input
                type="text"
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g. Technical"
              />
            </div>
            <div className="form-group">
              <label htmlFor="assignee" className="form-label">Assignee</label>
              <select id="assignee" name="assignee" value={formData.assignee} onChange={handleChange} className="form-input">
                <option value="">Unassigned</option>
                {users.map((u) => (
                  <option key={u._id || u.id} value={u._id || u.id}>
                    {u.name || u.userName || u.userEmail || u.email || 'Unknown'}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="customer" className="form-label">Customer</label>
              <select id="customer" name="customer" value={formData.customer} onChange={handleChange} className="form-input">
                <option value="">Select customer</option>
                {customers.map((c) => (
                  <option key={c._id || c.id} value={c._id || c.id}>
                    {c.customerName || c.customerCode || 'Unknown'}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="dueDate" className="form-label">Due Date</label>
              <input
                type="date"
                id="dueDate"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                className="form-input"
              />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="labels" className="form-label">Labels (comma-separated)</label>
              <input
                type="text"
                id="labels"
                name="labels"
                value={formData.labels}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g. urgent, backend"
              />
            </div>
          </div>

          <div className="section-header" style={{ marginTop: 24 }}>
            <h3><Paperclip size={18} /> Attachments</h3>
          </div>
          <div className="form-group">
            <label className="form-label">Upload Documents</label>
            <div className="file-upload-area">
              <input
                type="file"
                id="fileInputEdit"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt"
                onChange={handleFileSelect}
                className="file-input-hidden"
              />
              <label htmlFor="fileInputEdit" className="file-upload-label">
                <Upload size={24} />
                <span>Click to upload or drag and drop</span>
                <span className="file-upload-hint">PDF, DOC, XLS, images up to 5MB • Max {MAX_FILES} files</span>
              </label>
            </div>
            {attachments.length > 0 && (
              <ul className="attachment-list">
                {attachments.map((a, idx) => (
                  <li key={idx} className="attachment-item">
                    <Paperclip size={14} />
                    <span>{a.fileName}</span>
                    <span className="attachment-size">({a.size ? `${(a.size / 1024).toFixed(1)} KB` : '—'})</span>
                    <button type="button" className="attachment-remove" onClick={() => removeAttachment(idx)} title="Remove">
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => navigate('/support/support-tickets')}>
              <ArrowLeft size={16} />
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              <Save size={16} />
              {loading ? 'Saving...' : 'Update Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default SupportTicketEdit
